
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import {
  getGroupBySlug,
  getProductsByGroup,
  getAVOIPProductsBySpeed
} from "@/data/products";
import { useLanguage } from "@/contexts/LanguageContext";
import { useEffect } from "react";

const ProductGroup = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const speed = searchParams.get("speed") as "1gb" | "10gb" | "vpx-accessories" | "software" | null;
  const { t } = useLanguage();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const group = slug ? getGroupBySlug(slug) : null;
  
  let products = [];
  if (group) {
    if (group.id === "avoip") {
      products = speed ? getAVOIPProductsBySpeed(speed) : getProductsByGroup(group.id);
    } else {
      products = getProductsByGroup(group.id);
    }
  }

  if (!group) {
    return <div>{t('group.not.found')}</div>;
  }

  if (products.length === 0) {
    console.log('No products found for group:', group.id, 'with speed:', speed);
  }

  const getBackLink = () => {
    if (group.id === "avoip") return "/avoip-options";
    if (group.id.startsWith("reax")) return "/reax-options";
    if (group.id.startsWith("audio")) return "/audio-options";
    return "/";
  };

  const getBackText = () => {
    if (group.id === "avoip") return t('back.to.avoip');
    if (group.id.startsWith("reax")) return t('back.to.reax');
    if (group.id.startsWith("audio")) return t('back.to.audio');
    return t('back.to.categories');
  };

  // Check if this is the 1Gb AVOIP page to show Features & Benefits button
  const show1GbFeaturesButton = group.id === "avoip" && speed === "1gb";

  // Check if this is the Meeting Room Solutions page to show Features & Benefits button
  const showMeetingRoomFeaturesButton = group.id === "meeting-room";

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="mb-8">
        <Link
          to={getBackLink()}
          className="mb-8 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {getBackText()}
        </Link>
        <h1 className="mb-4 text-3xl font-bold tracking-tight">
          {t(group.name)} {speed && `(${speed.toUpperCase()})`}
        </h1>
        <p className="text-lg text-muted-foreground">{t(group.description)}</p>
        
        {/* Features & Benefits Button for 1Gb AVOIP */}
        {show1GbFeaturesButton && (
          <div className="mt-6">
            <Link to="/features-benefits-1gb">
              <Button size="lg" className="mb-8">
                1Gb Products - Features & Benefits
              </Button>
            </Link>
          </div>
        )}

        {/* Features & Benefits Button for Meeting Room Solutions */}
        {showMeetingRoomFeaturesButton && (
          <div className="mt-6">
            <Link to="/features-benefits-rxt4d">
              <Button size="lg" className="mb-8">
                Meeting Room / ReAX Room Solutions - Features & Benefits
              </Button>
            </Link>
          </div>
        )}
      </div>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product) => (
          <ProductCard
            key={product.id}
            title={product.name}
            subtitle={product.subtitle}
            description={product.description}
            images={product.images}
            image={product.image}
            link={`/product/${product.slug}`}
            className="animate-fade-up"
            isSoftware={product.networkSpeed === "software"}
            showVPXLogos={product.id === "DIDO-X4"}
          />
        ))}
      </div>
    </div>
  );
};

export default ProductGroup;
